import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AlertPrefs, AlertRow, CaptionScheduleIn, CommandAction, ConsoleRole, Device, DeviceId, ExportPayload, MemberScope, Role, WordLibrary, AlertChannelIn } from '@glyph/protocol-ts';
import { COMMAND_META, roleCan } from '@glyph/protocol-ts';
import { qk, useApi, useClient, useDeviceId } from './provider';
import type { LookPushIn, PresetIn, ShowEventIn } from '../api';
import type { HotspotDefault, UnitHotspot } from '../api';
import { applyLiveEvent } from '../adapters/cloud';
import type { CommandClient } from '../client/types';

/** The device-local shell (served by the agent) vs the cloud console; true for a MockClient in local mode too. */
export const isLocalShell = (c: CommandClient): boolean => c.selfDeviceId !== null;
export function useIsLocalShell(): boolean {
  return isLocalShell(useClient());
}

/**
 * May the signed-in user run `action`? RBAC from the shared command registry. The local shell has its own
 * accounts (admin / tech) enforced by the agent, so it always shows the control and lets the agent answer.
 */
export function useCan(action: CommandAction): boolean {
  const client = useClient();
  const local = isLocalShell(client);
  const api = useApi();
  const me = useQuery({ queryKey: qk.me, queryFn: () => api.me(), retry: false, enabled: !local });
  if (local) return true;
  return me.data ? consoleRoleCan(me.data.role, action) : false;
}

/** The cloud's rule for each console role (the server enforces it; this only hides what would be refused).
 * room_operator: read-only actions plus settings.patch (its screen only sends the caption position).
 * event_admin: what an admin may do on a unit, except updates and the destructive identity actions. */
export function consoleRoleCan(role: ConsoleRole, action: CommandAction): boolean {
  if (role === 'room_operator') return action === 'settings.patch' || action === 'system.reboot' || action === 'system.shutdown' || COMMAND_META[action]?.minRole === 'viewer';
  if (role === 'event_admin') {
    // no updates, no identity / wipe / lock, and never the Deepgram key (the Deepgram settings stay theirs)
    if (action.startsWith('update.') || action.startsWith('identity.') || action.startsWith('credentials.') || action === 'device.wipe' || action === 'device.lock') return false;
    return roleCan('admin', action);
  }
  return roleCan(role, action);
}

export function useMe() {
  const api = useApi();
  return useQuery({ queryKey: qk.me, queryFn: () => api.me(), retry: false });
}
/** Rename a unit (operator+): one free-form name for fleet, OLED and mDNS; the hotspot SSID follows its slug. */
export function useRenameDevice(id?: DeviceId) {
  const api = useApi();
  const qc = useQueryClient();
  const deviceId = useDeviceId(id);
  const m = useMutation({
    mutationFn: (name: string) => api.renameDevice(deviceId, name),
    onSuccess: () => {
      for (const key of [['devices'], qk.device(deviceId), qk.status(deviceId), qk.settings(deviceId), qk.network(deviceId)]) void qc.invalidateQueries({ queryKey: key });
    },
  });
  return { rename: m.mutateAsync, isPending: m.isPending, error: m.error };
}

export function useAuthStatus() {
  const api = useApi();
  return useQuery({ queryKey: qk.authStatus, queryFn: () => api.authStatus(), retry: false });
}

export function useDevices(params?: { state?: string; q?: string }) {
  const api = useApi();
  const client = useClient();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.devices(params), queryFn: () => api.devices(params), refetchInterval: 15_000 });
  // Presence/health/telemetry/levels/update pushes patch the list in place so the fleet table never waits for a
  // refetch. Subscriptions follow the SET of unit ids, not the list object: every push replaces the list, and
  // re-subscribing on each one meant tearing down and re-opening every unit's feed many times a second.
  const idsKey = (q.data ?? [])
    .map((d) => d.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (client.kind === 'local' || !idsKey) return;
    const unsubs = idsKey.split(',').map((id) =>
      client.subscribe(id, ['presence', 'telemetry', 'health', 'levels', 'update_status'], (ev) => {
        qc.setQueryData<Device[]>(qk.devices(params), (list) =>
          list?.map((d) => (d.id === ev.deviceId ? applyLiveEvent(d, ev) : d)),
        );
      }),
    );
    return () => unsubs.forEach((u) => u());
  }, [client, qc, idsKey, params]);
  return q;
}

export function useDevice(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.device(deviceId), queryFn: () => api.device(deviceId), refetchInterval: 15_000 });
}

/** Telemetry history for the Overview chart (cloud). */
export function useTelemetryHistory(id?: DeviceId, sinceMinutes = 60) {
  const api = useApi();
  const client = useClient();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: ['telemetry', deviceId, sinceMinutes], queryFn: () => api.telemetry(deviceId, sinceMinutes), enabled: !isLocalShell(client) && !!deviceId, refetchInterval: 30_000 });
}

export function useSecurity(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.security(deviceId), queryFn: () => api.security(deviceId) });
}
export function useNetwork(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.network(deviceId), queryFn: () => api.network(deviceId), refetchInterval: 10_000 });
}
export function useLogs(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.logs(deviceId), queryFn: () => api.logs(deviceId), refetchInterval: 5_000 });
}
export function useCloudStatus(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.cloud(deviceId), queryFn: () => api.cloudStatus(deviceId), refetchInterval: 10_000 });
}
export function useSystemInfo(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.sysinfo(deviceId), queryFn: () => api.systemInfo(deviceId) });
}
export function useUpdateStatus(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  return useQuery({ queryKey: qk.update(deviceId), queryFn: () => api.updateStatus(deviceId) });
}

export function useWordLibrary(id?: DeviceId) {
  const api = useApi();
  const qc = useQueryClient();
  const deviceId = useDeviceId(id);
  const q = useQuery({ queryKey: qk.wordlib(deviceId), queryFn: () => api.wordLibrary(deviceId) });
  const save = useMutation({
    mutationFn: (lib: WordLibrary) => api.setWordLibrary(deviceId, lib),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.wordlib(deviceId) }),
  });
  return { ...q, library: q.data?.library, budget: q.data?.budget, save: save.mutateAsync, isSaving: save.isPending };
}

export function useRecordings(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.recordings(id), queryFn: () => api.recordings(id) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['recordings'] });
  const rename = useMutation({ mutationFn: (v: { name: string; display_name: string }) => api.renameRecording(deviceId, v.name, v.display_name), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (name: string) => api.deleteRecording(deviceId, name), onSuccess: invalidate });
  const exp = useMutation({ mutationFn: (p: ExportPayload) => api.exportRecording(deviceId, p) });
  return { ...q, rename: rename.mutateAsync, remove: remove.mutateAsync, export: exp.mutateAsync, isExporting: exp.isPending };
}

export function useAlerts(open?: boolean) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.alerts(open), queryFn: () => api.alerts(open), refetchInterval: 15_000 });
  const ack = useMutation({ mutationFn: (a: AlertRow) => api.ackAlert(a), onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }) });
  const channels = useQuery({ queryKey: qk.alertChannels, queryFn: () => api.alertChannels(), retry: false });
  return { ...q, ack: ack.mutateAsync, channels: channels.data };
}

/** Alert routing targets (cloud) with the mutations the Routing card needs. */
export function useAlertChannels() {
  const api = useApi();
  const qc = useQueryClient();
  const client = useClient();
  const enabled = !isLocalShell(client);
  const q = useQuery({ queryKey: ['alert-channels'], queryFn: () => api.channels(), enabled, retry: false });
  const delivery = useQuery({ queryKey: ['alert-delivery'], queryFn: () => api.alertDelivery(), enabled, retry: false, staleTime: 5 * 60_000 });
  const inv = () => void qc.invalidateQueries({ queryKey: ['alert-channels'] });
  const create = useMutation({ mutationFn: (body: AlertChannelIn) => api.createChannel(body), onSuccess: inv });
  const update = useMutation({ mutationFn: (v: { id: string; body: AlertChannelIn }) => api.updateChannel(v.id, v.body), onSuccess: inv });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteChannel(id), onSuccess: inv });
  const test = useMutation({ mutationFn: (id: string) => api.testChannel(id) });
  return { ...q, delivery: delivery.data, create: create.mutateAsync, update: update.mutateAsync, remove: remove.mutateAsync, test: test.mutateAsync, busy: create.isPending || update.isPending || remove.isPending, testing: test.isPending };
}

export function useReleases(channel: 'stable' | 'beta' = 'stable', kind: 'app' | 'os' = 'app') {
  const api = useApi();
  return useQuery({ queryKey: qk.releases(channel, kind), queryFn: () => api.releases(channel, kind) });
}

export function useRollout(rolloutId?: string) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.rollout(rolloutId ?? ''),
    queryFn: () => api.rollout(rolloutId!),
    enabled: !!rolloutId,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 500 : false),
  });
  const create = useMutation({
    mutationFn: (body: Parameters<typeof api.createRollout>[0]) => api.createRollout(body),
    onSuccess: (ro) => {
      qc.setQueryData(qk.rollout(ro.id), ro);
      void qc.invalidateQueries({ queryKey: qk.rollouts });
    },
  });
  return { ...q, create: create.mutateAsync, isCreating: create.isPending, created: create.data };
}

/** Lock / unlock updates on one, several or all devices (admin). Refreshes the fleet list and the device settings. */
/** Push a Deepgram key to all / selected units (admin); queued for offline ones. */
export function useSendDeepgramKey() {
  const api = useApi();
  const m = useMutation({ mutationFn: (body: { api_key: string; device_ids?: DeviceId[]; all?: boolean }) => api.sendDeepgramKey(body) });
  return { send: m.mutateAsync, isPending: m.isPending, error: m.error };
}

/** Fleet "Apply look": after a push the targets' settings (and preset lists) are stale. */
export function usePushLook() {
  const api = useApi();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (body: LookPushIn) => api.pushLook(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['profiles'] });
    },
  });
  return { push: m.mutateAsync, isPending: m.isPending, error: m.error };
}

export function useLockUpdates() {
  const api = useApi();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (body: { device_ids?: DeviceId[]; all?: boolean; locked: boolean }) => api.lockUpdates(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['devices'] });
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  return { lock: m.mutateAsync, isPending: m.isPending, error: m.error };
}

export function useMembers(enabled = true) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.members, queryFn: () => api.members(), enabled });
  const inv = () => qc.invalidateQueries({ queryKey: qk.members });
  const invite = useMutation({ mutationFn: (v: { email: string; role: ConsoleRole }) => api.invite(v.email, v.role as Role), onSuccess: inv });
  const setRole = useMutation({ mutationFn: (v: { id: string; role: ConsoleRole }) => api.setRole(v.id, v.role), onSuccess: inv });
  const remove = useMutation({ mutationFn: (memberId: string) => api.removeMember(memberId), onSuccess: inv });
  const create = useMutation({
    mutationFn: (v: { username: string; password: string; name?: string; role: ConsoleRole; scope?: MemberScope | null }) => api.createMember(v),
    onSuccess: inv,
  });
  const patch = useMutation({ mutationFn: (v: { id: string; name?: string; username?: string; password?: string }) => api.patchMember(v.id, v), onSuccess: inv });
  const setScope = useMutation({ mutationFn: (v: { id: string; scope: MemberScope }) => api.setMemberScope(v.id, v.scope), onSuccess: inv });
  return {
    ...q,
    invite: invite.mutateAsync,
    setRole: setRole.mutateAsync,
    remove: remove.mutateAsync,
    create: create.mutateAsync,
    patch: patch.mutateAsync,
    setScope: setScope.mutateAsync,
    busy: create.isPending || patch.isPending || setScope.isPending,
  };
}

/** Cloud preset library: list + edit + capture from a unit + import a unit's presets + apply to units. */
export function usePresets(enabled = true) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['presets'], queryFn: () => api.presets(), enabled });
  const inv = () => qc.invalidateQueries({ queryKey: ['presets'] });
  const create = useMutation({ mutationFn: (v: PresetIn) => api.createPreset(v), onSuccess: inv });
  const update = useMutation({ mutationFn: (v: { id: string } & Partial<PresetIn>) => api.updatePreset(v.id, v), onSuccess: inv });
  const remove = useMutation({ mutationFn: (id: string) => api.deletePreset(id), onSuccess: inv });
  const capture = useMutation({
    mutationFn: (v: { device_id: DeviceId; name: string; description?: string; sections: string[]; overwrite?: boolean }) => api.capturePreset(v),
    onSuccess: inv,
  });
  const importFrom = useMutation({ mutationFn: (v: { device_id: DeviceId; overwrite?: boolean }) => api.importPresets(v), onSuccess: inv });
  const apply = useMutation({
    mutationFn: (v: { id: string; device_ids?: DeviceId[]; all?: boolean; save_on_unit?: boolean }) => api.applyPreset(v.id, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['profiles'] });
    },
  });
  return {
    ...q,
    create: create.mutateAsync,
    update: update.mutateAsync,
    remove: remove.mutateAsync,
    capture: capture.mutateAsync,
    importFrom: importFrom.mutateAsync,
    apply: apply.mutateAsync,
    busy: create.isPending || update.isPending || remove.isPending || capture.isPending || importFrom.isPending || apply.isPending,
  };
}

export function useAudit(limit = 200) {
  const api = useApi();
  return useQuery({ queryKey: [...qk.audit, limit], queryFn: () => api.audit(limit) });
}

/** Pending-unit approval and claim codes (platform staff). */
export function useFleetAdmin() {
  const api = useApi();
  const qc = useQueryClient();
  const inv = () => {
    void qc.invalidateQueries({ queryKey: ['devices'] });
  };
  const approve = useMutation({ mutationFn: (v: { id: DeviceId; name?: string; event_id?: string | null }) => api.approveDevice(v.id, v), onSuccess: inv });
  const reject = useMutation({ mutationFn: (id: DeviceId) => api.rejectDevice(id), onSuccess: inv });
  const claim = useMutation({ mutationFn: (v: { name?: string; event_id?: string | null; ttl_minutes?: number }) => api.mintClaimCode(v) });
  return { approve: approve.mutateAsync, reject: reject.mutateAsync, claim: claim.mutateAsync, busy: approve.isPending || reject.isPending || claim.isPending };
}

/** Shows (cloud events) with the admin mutations; the local shell has none. */
export function useEvents() {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.events, queryFn: () => api.events(), retry: false });
  const inv = () => {
    void qc.invalidateQueries({ queryKey: qk.events });
    void qc.invalidateQueries({ queryKey: ['devices'] });
  };
  const create = useMutation({ mutationFn: (b: ShowEventIn) => api.createEvent(b), onSuccess: inv });
  const update = useMutation({ mutationFn: (v: { id: string; body: ShowEventIn }) => api.updateEvent(v.id, v.body), onSuccess: inv });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteEvent(id), onSuccess: inv });
  const assign = useMutation({ mutationFn: (v: { deviceId: DeviceId; eventId: string | null }) => api.assignDevice(v.deviceId, v.eventId), onSuccess: inv });
  return { ...q, create: create.mutateAsync, update: update.mutateAsync, remove: remove.mutateAsync, assign: assign.mutateAsync, busy: create.isPending || update.isPending || remove.isPending || assign.isPending };
}

/** Caption schedules (cloud): after-hours OFF windows. Empty on the local shell. */
export function useSchedules(enabled = true) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['schedules'], queryFn: () => api.schedules(), enabled, retry: false });
  const inv = () => {
    void qc.invalidateQueries({ queryKey: ['schedules'] });
    void qc.invalidateQueries({ queryKey: ['schedule-state'] });
  };
  const create = useMutation({ mutationFn: (b: CaptionScheduleIn) => api.createSchedule(b), onSuccess: inv });
  const update = useMutation({ mutationFn: (v: { id: string; body: CaptionScheduleIn }) => api.updateSchedule(v.id, v.body), onSuccess: inv });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteSchedule(id), onSuccess: inv });
  return { ...q, create: create.mutateAsync, update: update.mutateAsync, remove: remove.mutateAsync, busy: create.isPending || update.isPending || remove.isPending };
}

/** The schedule badge for one unit: off until / off from, and whether a person overrode it. */
export function useDeviceSchedule(id?: DeviceId) {
  const api = useApi();
  const deviceId = useDeviceId(id);
  const local = useIsLocalShell();
  return useQuery({ queryKey: ['schedule-state', deviceId], queryFn: () => api.deviceSchedule(deviceId), enabled: !local && !!deviceId, refetchInterval: 30_000, retry: false });
}

/** Per-device notification arming (cloud). Disabled on the local shell, which has no notifications. */
export function useAlertPrefs(id?: DeviceId) {
  const api = useApi();
  const client = useClient();
  const qc = useQueryClient();
  const deviceId = useDeviceId(id);
  const key = ['alertPrefs', deviceId] as const;
  const q = useQuery({ queryKey: key, queryFn: () => api.alertPrefs(deviceId), enabled: !isLocalShell(client), retry: false });
  const m = useMutation({
    mutationFn: (body: Pick<AlertPrefs, 'arm_mode' | 'snooze_until' | 'muted_kinds'>) => api.setAlertPrefs(deviceId, body),
    onSuccess: (data) => qc.setQueryData(key, data),
  });
  return { ...q, save: m.mutateAsync, isSaving: m.isPending };
}

/** Organisation hotspot default (admin / owner). */
export function useHotspotDefault(enabled = true) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery<HotspotDefault>({ queryKey: ['hotspot-default'], queryFn: () => api.hotspotDefault(), enabled, retry: false });
  const set = useMutation({
    mutationFn: (password: string) => api.setHotspotDefault(password),
    onSuccess: (d) => {
      qc.setQueryData(['hotspot-default'], d);
      void qc.invalidateQueries({ queryKey: ['hotspot'] });
    },
  });
  return { ...q, set: set.mutateAsync, busy: set.isPending };
}

/** One unit's cloud-managed hotspot password (admin / owner, event admin on its units). */
export function useUnitHotspot(id: DeviceId, enabled = true) {
  const api = useApi();
  const qc = useQueryClient();
  const q = useQuery<UnitHotspot>({ queryKey: ['hotspot', id], queryFn: () => api.unitHotspot(id), enabled: enabled && !!id, retry: false, refetchInterval: 15_000 });
  const set = useMutation({
    mutationFn: (password: string | null) => api.setUnitHotspot(id, password),
    onSuccess: (d) => {
      qc.setQueryData(['hotspot', id], d);
      void qc.invalidateQueries({ queryKey: ['hotspot-default'] });
    },
  });
  return { ...q, set: set.mutateAsync, busy: set.isPending };
}
